import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Lock, Key, Eye, EyeOff } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import type { CompanyProfile, ChangePassword } from "@shared/schema";

// Password change form schema
const passwordChangeSchema = z.object({
  currentPassword: z.string().optional(),
  newPassword: z.string()
    .min(8, "Passwort muss mindestens 8 Zeichen lang sein")
    .regex(/[A-Z]/, "Passwort muss mindestens einen Großbuchstaben enthalten")
    .regex(/[a-z]/, "Passwort muss mindestens einen Kleinbuchstaben enthalten")
    .regex(/[0-9]/, "Passwort muss mindestens eine Zahl enthalten"),
  confirmPassword: z.string(),
}).refine(data => data.newPassword === data.confirmPassword, {
  message: "Passwörter stimmen nicht überein",
  path: ["confirmPassword"],
});

type PasswordChangeForm = z.infer<typeof passwordChangeSchema>;

export default function PasswortAendern() {
  const { toast } = useToast();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Fetch company profile to check if password exists
  const { data: company, isLoading } = useQuery<CompanyProfile>({
    queryKey: ['/api/companies/me']
  });

  // Password change form
  const passwordForm = useForm<PasswordChangeForm>({
    resolver: zodResolver(passwordChangeSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Password change mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data: PasswordChangeForm) => {
      const passwordData: ChangePassword = {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
        confirmPassword: data.confirmPassword,
      };
      return apiRequest('POST', '/api/companies/me/password', passwordData);
    },
    onSuccess: () => {
      toast({
        title: "Passwort geändert",
        description: "Ihr Passwort wurde aktualisiert.",
      });
      passwordForm.reset();
    },
    onError: (error: any) => {
      toast({
        title: "Fehler beim Passwort ändern",
        description: error.message || "Das Passwort konnte nicht geändert werden.",
        variant: "destructive",
      });
    },
  });

  const onSubmitPassword = (data: PasswordChangeForm) => {
    changePasswordMutation.mutate(data);
  };

  const hasPassword = company?.passwordUpdatedAt;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-8">
      <div className="flex items-center space-x-2">
        <Lock className="h-6 w-6" />
        <h1 className="text-3xl font-bold font-playfair">Passwort ändern</h1>
      </div>

      {/* Password Management Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Lock className="h-5 w-5" />
            <CardTitle>Passwort-Verwaltung</CardTitle>
          </div>
          <CardDescription>
            {hasPassword ? "Ändern Sie Ihr Passwort" : "Richten Sie ein Passwort für Ihr Firmenkonto ein"}
            {hasPassword && (
              <div className="flex items-center space-x-2 mt-2">
                <Badge variant="outline" data-testid="badge-password-updated">
                  Letztes Update: {new Date(company.passwordUpdatedAt!).toLocaleDateString('de-DE')}
                </Badge>
              </div>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={passwordForm.handleSubmit(onSubmitPassword)} className="space-y-4">
            {hasPassword && (
              <div className="space-y-2">
                <Label htmlFor="currentPassword" data-testid="label-current-password">
                  Aktuelles Passwort <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    data-testid="input-current-password"
                    {...passwordForm.register("currentPassword")}
                    type={showCurrentPassword ? "text" : "password"}
                    placeholder="Aktuelles Passwort"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    data-testid="button-toggle-current-password"
                  >
                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {passwordForm.formState.errors.currentPassword && (
                  <p className="text-sm text-red-500" data-testid="error-current-password">
                    {passwordForm.formState.errors.currentPassword.message}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="newPassword" data-testid="label-new-password">
                {hasPassword ? "Neues Passwort" : "Passwort"} <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  data-testid="input-new-password"
                  {...passwordForm.register("newPassword")}
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Mindestens 8 Zeichen"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  data-testid="button-toggle-new-password"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {passwordForm.formState.errors.newPassword && (
                <p className="text-sm text-red-500" data-testid="error-new-password">
                  {passwordForm.formState.errors.newPassword.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" data-testid="label-confirm-password">
                Passwort bestätigen <span className="text-red-500">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  data-testid="input-confirm-password"
                  {...passwordForm.register("confirmPassword")}
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Passwort wiederholen"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  data-testid="button-toggle-confirm-password"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {passwordForm.formState.errors.confirmPassword && (
                <p className="text-sm text-red-500" data-testid="error-confirm-password">
                  {passwordForm.formState.errors.confirmPassword.message}
                </p>
              )}
            </div>

            <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-md">
              <div className="flex items-start space-x-2">
                <Key className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-700 dark:text-blue-300">
                  <p className="font-medium">Passwort-Anforderungen:</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Mindestens 8 Zeichen</li>
                    <li>Mindestens ein Großbuchstabe</li>
                    <li>Mindestens ein Kleinbuchstabe</li>
                    <li>Mindestens eine Zahl</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                data-testid="button-change-password"
                disabled={changePasswordMutation.isPending}
                className="min-w-32"
              >
                {changePasswordMutation.isPending ? "Speichert..." : hasPassword ? "Passwort ändern" : "Passwort einrichten"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
